import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { getDeviceToken, setDeviceToken, fetchQuickStatus } from "@/lib/device";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MonitorSmartphone, KeyRound, ShieldOff, ChevronRight } from "lucide-react";
import { OneTimeCredentials } from "@/components/OneTimeCredentials";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
const digits = (v) => v.replace(/\D/g, "").slice(0, 4);

// Security page section: Quick Login PIN + Trusted NEEC Terminals (Owner manages/revokes with step-up).
export default function QuickLoginSecurity({ sec, onChanged }) {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [pinDlg, setPinDlg] = useState(false);
  const [f, setF] = useState({ password: "", code: "", pin: "", confirm_pin: "" });
  const [revoke, setRevoke] = useState(null);
  const [rv, setRv] = useState({ password: "", code: "" });
  const [err, setErr] = useState("");
  const [otc, setOtc] = useState(null);
  const [name, setName] = useState("");
  const thisToken = getDeviceToken();
  const [qs, setQs] = useState(null);
  useEffect(() => { fetchQuickStatus().then(setQs); }, [user?.id]);
  const isOwner = user?.role === "owner";
  const [showRevoked, setShowRevoked] = useState(false);
  const active = devices.filter((d) => d.status === "active");
  const revoked = devices.filter((d) => d.status !== "active");

  const load = () => (isOwner ? api.get("/auth/devices").then((r) => setDevices(r.data)).catch(() => setDevices([])) : setDevices([]));
  useEffect(() => { load(); }, [isOwner, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const trust = async () => {
    try {
      const { data } = await api.post("/auth/devices/trust", { name: name || "This terminal" });
      setDeviceToken(data.device_token); setName(""); load();
      window.dispatchEvent(new Event("neec-device-trusted"));
      toast.success("This device is now a Trusted NEEC Terminal. Quick Login enabled");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const savePin = async (generate = false) => {
    setErr("");
    try {
      const body = generate || !isOwner ? { password: f.password, code: f.code || null, generate: true } : { ...f, code: f.code || null };
      const { data } = await api.post("/auth/login-pin/set", body);
      toast.success("Login PIN saved"); setPinDlg(false); setF({ password: "", code: "", pin: "", confirm_pin: "" }); onChanged?.();
      if ((generate || !isOwner) && data.issued_credentials) setOtc(data.issued_credentials);
    }
    catch (e) { setErr(apiError(e.response?.data?.detail)); }
  };
  const doRevoke = async () => {
    setErr("");
    try {
      await api.post(`/auth/devices/${revoke.id}/revoke`, { password: rv.password, code: rv.code || null });
      toast.success("Device trust revoked"); setRevoke(null); setRv({ password: "", code: "" }); load();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <Card className="p-6" data-testid="sec-quick-login">
        <p className="font-head font-bold flex items-center gap-2 mb-2"><KeyRound className="w-5 h-5" /> Quick Login PIN</p>
        <p className="text-xs text-muted-foreground mb-3">Your personal 4-digit Login PIN for fast sign-in and user switching on trusted terminals. It is separate from the 6-digit Authorization PIN and never authorizes refunds, discounts, write-offs, credit changes or deletions.{!isOwner ? " Login PINs are generated by the system and shown to you once." : ""}</p>
        <div className="flex items-center gap-3">
          <span className="text-sm">Status: {sec?.has_login_pin ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px]">Set{sec?.login_pin_changed_at ? ` · ${fmt(sec.login_pin_changed_at)}` : ""}</Badge> : <Badge variant="outline" className="text-[10px]">Not set</Badge>}</span>
          <Button variant="outline" onClick={() => setPinDlg(true)} data-testid="sec-login-pin-btn">{sec?.has_login_pin ? "Change Login PIN" : "Set Login PIN"}</Button>
        </div>
      </Card>

      <Card className="p-6" data-testid="sec-trusted-devices">
        <p className="font-head font-bold flex items-center gap-2 mb-2"><MonitorSmartphone className="w-5 h-5" /> Trusted NEEC Terminals</p>
        <p className="text-xs text-muted-foreground mb-3">Quick Login (staff grid + PIN) only works on trusted devices. New or untrusted devices always require full email &amp; password sign-in.</p>
        {qs?.trust_source === "pos_register" ? (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 mb-3 text-sm" data-testid="sec-trusted-via-register">
            <p className="font-semibold text-emerald-600">Trusted through POS Register: {qs.register_name}{qs.location_name ? ` · ${qs.location_name}` : ""}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Status: Ready for Quick Login. No separate trust step is needed. <Link to="/settings" className="text-emerald-600 hover:underline">Manage POS Registers</Link></p>
          </div>
        ) : thisToken || qs?.quick_login_available ? <p className="text-sm text-emerald-600 mb-3" data-testid="sec-this-device-trusted">✓ This device is a trusted terminal.</p> : !isOwner ? (
          <p className="text-xs text-muted-foreground mb-3" data-testid="sec-trust-owner-only">Only the Owner can register this device as a trusted terminal.</p>
        ) : (
          <div className="flex gap-2 mb-3 max-w-md">
            <Input placeholder="Terminal name (e.g. Front Counter 1)" value={name} onChange={(e) => setName(e.target.value)} data-testid="sec-device-name" />
            <Button onClick={trust} className="bg-violet-600 hover:bg-violet-700 text-white shrink-0" data-testid="sec-trust-device-btn">Trust this device</Button>
          </div>
        )}
        {isOwner && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Active Trusted Terminals ({active.length})</p>
            {active.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`device-${d.id}`}>
                <span className="font-medium truncate min-w-[8rem] flex-1">{d.name}</span>
                <span className="hidden lg:inline text-[11px] text-muted-foreground whitespace-nowrap">Trusted {fmt(d.created_at)}</span>
                <span className="hidden sm:inline text-[11px] text-muted-foreground whitespace-nowrap">Last used {fmt(d.last_used_at)}{d.last_user ? ` · ${d.last_user}` : ""}</span>
                <Button size="sm" variant="outline" className="h-7 px-2 border-rose-500/40 text-rose-600 shrink-0" onClick={() => { setErr(""); setRevoke(d); }} data-testid={`revoke-device-${d.id}`}><ShieldOff className="w-3.5 h-3.5 mr-1" /> Revoke</Button>
              </div>
            ))}
            {active.length === 0 && <p className="text-xs text-muted-foreground">No active trusted terminals.</p>}
            {revoked.length > 0 && (
              <div className="pt-2" data-testid="revoked-devices-section">
                <button type="button" onClick={() => setShowRevoked((v) => !v)} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground" data-testid="revoked-devices-toggle">
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showRevoked ? "rotate-90" : ""}`} /> Revoked / Previous Terminals ({revoked.length}) <span className="normal-case font-normal">· {showRevoked ? "Hide" : "Show"}</span>
                </button>
                {showRevoked && (
                  <div className="mt-2 space-y-1" data-testid="revoked-devices-list">
                    {revoked.map((d) => (
                      <div key={d.id} className="flex items-center gap-3 rounded-lg border border-dashed border-border px-3 py-1.5 text-xs text-muted-foreground" data-testid={`device-${d.id}`}>
                        <span className="truncate min-w-0 flex-1">{d.name} <Badge className="ml-1 bg-rose-500/15 text-rose-500 text-[10px]">Revoked</Badge></span>
                        <span className="hidden md:inline whitespace-nowrap">Trusted {fmt(d.created_at)} by {d.created_by}</span>
                        <span className="hidden sm:inline whitespace-nowrap">Last used {fmt(d.last_used_at)}{d.last_user ? ` · ${d.last_user}` : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      <Dialog open={pinDlg} onOpenChange={(o) => { if (!o) setPinDlg(false); }}>
        <DialogContent className="max-w-md" data-testid="login-pin-dialog">
          <DialogHeader><DialogTitle className="font-head">{sec?.has_login_pin ? "Change" : "Set"} Quick Login PIN</DialogTitle>
            <DialogDescription>Verify with your password{sec?.mfa_enabled ? " and authenticator code" : ""}. {isOwner ? "Choose a 4-digit PIN (must differ from every other PIN in your business) or let NEEC generate one." : "A new 4-digit Login PIN will be generated and shown to you once."}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoFocus data-testid="lp-password" /></div>
            {sec?.mfa_enabled && <div><Label className="text-xs">Authenticator code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.trim() })} className="mono" data-testid="lp-code" /></div>}
            {isOwner && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">New login PIN (4 digits)</Label><Input type="password" inputMode="numeric" value={f.pin} onChange={(e) => setF({ ...f, pin: digits(e.target.value) })} className="mono" data-testid="lp-pin" /></div>
                <div><Label className="text-xs">Confirm</Label><Input type="password" inputMode="numeric" value={f.confirm_pin} onChange={(e) => setF({ ...f, confirm_pin: digits(e.target.value) })} className="mono" data-testid="lp-confirm" /></div>
              </div>
            )}
            {err && <p className="text-sm text-rose-500" data-testid="lp-error">{err}</p>}
            {isOwner
              ? <div className="grid grid-cols-2 gap-2">
                  <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => savePin(false)} data-testid="lp-save">Save Login PIN</Button>
                  <Button variant="outline" onClick={() => savePin(true)} data-testid="lp-generate">Generate for me</Button>
                </div>
              : <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={() => savePin(true)} data-testid="lp-save">Generate My Login PIN</Button>}
          </div>
        </DialogContent>
      </Dialog>
      <OneTimeCredentials creds={otc} staffName={user?.name} onClose={() => setOtc(null)} />

      <Dialog open={!!revoke} onOpenChange={(o) => { if (!o) setRevoke(null); }}>
        <DialogContent className="max-w-md" data-testid="revoke-device-dialog">
          <DialogHeader><DialogTitle className="font-head">Revoke trusted terminal</DialogTitle>
            <DialogDescription>Quick Login stops working on "{revoke?.name}" immediately. Confirm with your password{sec?.mfa_enabled ? " and authenticator code" : ""}.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Password</Label><Input type="password" value={rv.password} onChange={(e) => setRv({ ...rv, password: e.target.value })} autoFocus data-testid="rv-password" /></div>
            {sec?.mfa_enabled && <div><Label className="text-xs">Authenticator code</Label><Input value={rv.code} onChange={(e) => setRv({ ...rv, code: e.target.value.trim() })} className="mono" data-testid="rv-code" /></div>}
            {err && <p className="text-sm text-rose-500" data-testid="rv-error">{err}</p>}
            <Button className="w-full bg-rose-600 hover:bg-rose-700 text-white" onClick={doRevoke} data-testid="rv-confirm">Revoke</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
